import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { 
  CheckCircle, 
  ArrowRight,
  Shield,
  Globe,
  Star,
  Mail
} from 'lucide-react';

const PaymentSuccess = () => {
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      try {
        // Get session ID from URL params
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');
        
        if (!sessionId) {
          setError('No payment session found');
          setLoading(false);
          return;
        }

        // Check payment status with backend
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`);
        
        if (!response.ok) {
          throw new Error('Failed to verify payment');
        }
        
        const data = await response.json();
        setSessionData(data);
        setLoading(false);
        
        // Redirect based on membership level
        const ownerPortalUrl = process.env.REACT_APP_OWNER_PORTAL_URL || 'https://lpassociate.org';
        setTimeout(() => {
          if (data.membership_level === "Standard" || data.membership_level === "Premium") {
            // Redirect Standard/Premium members to Owner/Investor Portal
            window.location.href = ownerPortalUrl;
          } else {
            // Redirect Basic members to main site
            window.location.href = process.env.REACT_APP_REDIRECT_URL;
          }
        }, 10000); // 10 seconds to read portal URL
        
      } catch (err) {
        console.error('Payment verification error:', err);
        setError('Failed to verify payment status');
        setLoading(false);
      }
    };

    checkPaymentStatus();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Verifying Payment...</h2>
            <p className="text-slate-600">Please wait while we confirm your membership payment.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-red-600 text-xl">⚠️</span>
            </div>
            <h2 className="text-xl font-semibold text-red-800 mb-2">Payment Verification Error</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <Button 
              onClick={() => window.location.href = '/'}
              className="bg-red-600 hover:bg-red-700"
            >
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-slate-800 mb-4">
            Payment Successful! 🎉
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Welcome to Legacy Property Council! Your membership payment has been processed successfully 
            and your account is now fully activated.
          </p>
        </div>

        {/* Payment Details */}
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          
          {/* Payment Summary */}
          <Card className="shadow-lg">
            <CardHeader className="bg-gradient-to-r from-green-50 to-blue-50">
              <CardTitle className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span>Payment Confirmation</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              {sessionData && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Amount Paid:</span>
                    <span className="font-semibold text-green-600">
                      ${sessionData.amount?.toFixed(2)} {sessionData.currency?.toUpperCase()}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Payment Status:</span>
                    <span className="font-semibold text-green-600 flex items-center">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Completed
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Session ID:</span>
                    <code className="bg-slate-100 px-2 py-1 rounded text-xs">
                      {sessionData.session_id}
                    </code>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* What's Next */}
          <Card className="shadow-lg border-2 border-blue-400">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
              <CardTitle className="flex items-center space-x-2">
                <Globe className="w-5 h-5" />
                <span>🎉 Access Your Owner/Investor Portal</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <p className="text-slate-700 font-medium">
                  As a {sessionData?.membership_level || 'Standard/Premium'} member, you now have access to the full property management portal!
                </p>
                
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-900 mb-3">
                    <strong>Portal URL (Save this!):</strong>
                  </p>
                  <code className="block bg-white px-3 py-2 rounded text-sm text-blue-700 mb-3 break-all">
                    {process.env.REACT_APP_OWNER_PORTAL_URL || 'https://lpassociate.org'}
                  </code>
                  <p className="text-xs text-blue-700">
                    📧 We'll also send this link to your email for future reference
                  </p>
                </div>

                <Button
                  onClick={() => window.open(process.env.REACT_APP_OWNER_PORTAL_URL || 'https://lpassociate.org', '_blank')}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-6 text-lg"
                >
                  <Globe className="w-5 h-5 mr-2" />
                  Access Portal Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>

                <p className="text-xs text-center text-slate-500">
                  Auto-redirecting in 10 seconds... or click above to go now
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Membership Benefits */}
        <Card className="shadow-lg mb-8">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
            <CardTitle className="flex items-center space-x-2">
              <Star className="w-5 h-5" />
              <span>Your Active Membership Benefits</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div className="flex flex-col items-center space-y-2">
                <Globe className="w-8 h-8 text-blue-600" />
                <h4 className="font-semibold text-slate-800">Member Portal Access</h4>
                <p className="text-sm text-slate-600">
                  Full access to property management tools and tenant coordination
                </p>
              </div>
              
              <div className="flex flex-col items-center space-y-2">
                <Shield className="w-8 h-8 text-green-600" />
                <h4 className="font-semibold text-slate-800">Privacy Protection</h4>
                <p className="text-sm text-slate-600">
                  Your property affairs protected under PMA private contract law
                </p>
              </div>
              
              <div className="flex flex-col items-center space-y-2">
                <Mail className="w-8 h-8 text-amber-600" />
                <h4 className="font-semibold text-slate-800">Administrative Support</h4>
                <p className="text-sm text-slate-600">
                  Professional coordination and member support services
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Redirect Notice */}
        <Card className="shadow-lg bg-gradient-to-r from-blue-50 to-amber-50">
          <CardContent className="p-6 text-center">
            {sessionData?.membership_level === "Standard" || sessionData?.membership_level === "Premium" ? (
              <>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">
                  🚀 Redirecting to Portal Selection...
                </h3>
                <p className="text-slate-600 mb-4">
                  You'll be automatically redirected to select your preferred portal. 
                  Choose the portal that best fits your property management needs.
                </p>
                <Button 
                  onClick={() => window.location.href = process.env.REACT_APP_OWNER_PORTAL_URL || 'https://lpassociate.org'}
                  className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white"
                >
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Select Portal Now
                </Button>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">
                  🚀 Redirecting to Your Member Portal...
                </h3>
                <p className="text-slate-600 mb-4">
                  You'll be automatically redirected in a few seconds to set up your portal account. 
                  After setup, you can begin managing your properties immediately.
                </p>
                <Button 
                  onClick={() => window.location.href = process.env.REACT_APP_REDIRECT_URL}
                  className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white"
                >
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Go to Portal Now
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Support Contact */}
        <div className="text-center mt-8">
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            Questions about your membership? Contact our member support team at{' '}
            <a href="mailto:info@lpassociation.org" className="text-blue-600 hover:underline">
              info@lpassociation.org
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;